import { useSelector } from "react-redux";
import { selectCurrentRoomId } from "modules/room/redux/selectors";
import { useContext, useEffect, useState } from "react";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import {
  selectCurrentEvent,
  selectCurrentEventId,
  selectIsEventManager,
} from "modules/event/selectors";
import { makeSelectUsersInEvent } from "modules/event/usersInEvent/selectors";
import { selectUsersMicCamStatusInCurrentRoom } from "modules/userStatus/redux/selectors";
import { useDevices } from "modules/audioVideo/hooks";
import { getEventRole, isEventSpeaker } from "modules/event/utils/eventUtils";
import { selectUser } from "modules/auth/redux/selectors";

interface Relationship {
  startTime: number | null;
  duration: number;
}

export const MIN_RELATIONSHIP_DURATION_MS = 60 * 1000;

const useRelationshipsTracking = () => {
  const { track } = useContext(TRACKING_CONTEXT);
  const currentRoomId = useSelector(selectCurrentRoomId);
  const currentEventId = useSelector(selectCurrentEventId);
  const usersMicCamStatus = useSelector(selectUsersMicCamStatusInCurrentRoom);
  const usersInEvent = useSelector(makeSelectUsersInEvent);
  const { microphoneActive, cameraActive } = useDevices();
  const getIsUserCommunicating = (isMicOn?: boolean, isCamOn?: boolean) =>
    isMicOn || isCamOn;
  const currentEvent = useSelector(selectCurrentEvent);
  const isEventManager = useSelector(selectIsEventManager);
  const user = useSelector(selectUser);

  const isCurrentUserCommunicating = getIsUserCommunicating(
    microphoneActive,
    cameraActive,
  );

  const [relationships, setRelationships] = useState<
    Record<string, Relationship>
  >({});
  const [isStartedRelationships, setIsStartedRelationships] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (usersMicCamStatus) {
      const currentTime = Date.now();
      const usersInRoomRelationships: Record<string, Relationship> =
        Object.entries(usersMicCamStatus).reduce(
          (accumulator, [userId, userMicCamStatus]) => {
            const areBothUsersCommunicating =
              isCurrentUserCommunicating &&
              getIsUserCommunicating(
                userMicCamStatus?.isMicrophoneOn,
                userMicCamStatus?.isCameraOn,
              );

            const isUserInRelationships = relationships[userId] !== undefined;

            if (!isUserInRelationships) {
              if (areBothUsersCommunicating) {
                return {
                  ...accumulator,
                  [userId]: {
                    startTime: currentTime,
                    duration: 0,
                  },
                };
              }
              return {
                ...accumulator,
                [userId]: {
                  startTime: null,
                  duration: 0,
                },
              };
            }

            const prevRelationship = relationships[userId];
            if (!areBothUsersCommunicating) {
              return {
                ...accumulator,
                [userId]: {
                  ...prevRelationship,
                  startTime: null,
                  duration:
                    prevRelationship.startTime !== null
                      ? prevRelationship.duration +
                        (currentTime - prevRelationship.startTime)
                      : prevRelationship.duration,
                },
              };
            }

            if (prevRelationship.startTime === null) {
              return {
                ...accumulator,
                [userId]: {
                  ...prevRelationship,
                  startTime: currentTime,
                },
              };
            }

            return {
              ...accumulator,
              [userId]: relationships[userId],
            };
          },
          {},
        );

      // Users exited the room
      const exitedUsersRelationships = Object.keys(relationships)
        .filter((userId) => usersMicCamStatus[userId] === undefined)
        .reduce((accumulator, userId) => {
          const prevRelationship = relationships[userId];
          if (prevRelationship.startTime) {
            return {
              ...accumulator,
              [userId]: {
                ...prevRelationship,
                startTime: null,
                duration:
                  prevRelationship.duration +
                  currentTime -
                  prevRelationship.startTime,
              },
            };
          }

          return {
            ...accumulator,
            [userId]: prevRelationship,
          };
        }, {});

      setRelationships({
        ...relationships,
        ...usersInRoomRelationships,
        ...exitedUsersRelationships,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersMicCamStatus, microphoneActive, cameraActive]);

  useEffect(() => {
    if (currentEventId && currentRoomId) {
      const newIsStartedRelationships: Record<string, boolean> = Object.entries(
        relationships,
      )
        .filter(
          ([userId, relationship]) =>
            usersInEvent.get(userId) &&
            relationship.duration &&
            relationship.duration >= MIN_RELATIONSHIP_DURATION_MS &&
            !isStartedRelationships[userId],
        )
        .reduce((prevIsStartedRelationships, [userId, _]) => {
          const eventRole =
            currentEvent &&
            getEventRole(
              isEventManager,
              isEventSpeaker(currentEvent, user?.id, user?.email),
            );

          track(Events.RELATIONSHIP_STARTED, {
            event: currentEventId, // remove after complete migration to datagol
            eventId: currentEventId,
            table: currentRoomId,
            relationshipWithUser: usersInEvent.get(userId)?.email,
            eventRole,
          });
          return {
            ...prevIsStartedRelationships,
            [userId]: true,
          };
        }, {});

      setIsStartedRelationships({
        ...isStartedRelationships,
        ...newIsStartedRelationships,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationships]);

  return {
    relationships,
  };
};

export default useRelationshipsTracking;
